import { useCallback, useRef, useState } from "react";
import { getIcdDescription } from "../../../../api/icdDescription/getIcdDescription";
import { InfoIcon } from "../../../../svg/info-icon";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";

interface IcdDescriptionTooltipProps {
  icdCode: string;
}

const IcdDescriptionTooltip = ({ icdCode }: IcdDescriptionTooltipProps) => {
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(async () => {
    // Don't fetch if icdCode is empty or "--"
    if (!icdCode || icdCode === "--" || icdCode === "N/A") {
      return;
    }

    setShowTooltip(true);
    setIsLoading(true);
    setDescription("");

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const result = await getIcdDescription(
        icdCode,
        abortControllerRef.current.signal,
      );
      // Only set description if the request wasn't aborted
      if (result) {
        setDescription(result);
      }
    } finally {
      setIsLoading(false);
    }
  }, [icdCode]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
    setDescription("");
    setIsLoading(false);

    // Abort the ongoing request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Don't render if icdCode is empty or "--"
  if (!icdCode || icdCode === "--" || icdCode === "N/A") {
    return null;
  }

  const tooltipContent = isLoading
    ? "Loading..."
    : description || "No description available";

  return (
    <div
      ref={iconRef}
      className="inline-flex cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <InfoIcon
        width="14"
        height="14"
        stroke="#6B7280"
        className="hover:opacity-70"
      />
      <ToolTipUsingPortal
        showTooltip={showTooltip}
        parentRef={iconRef}
        placement="top"
        wrapText={true}
        maxWidth="300px"
        offsetPx={8}
      >
        <span className={isLoading ? "italic" : ""}>{tooltipContent}</span>
      </ToolTipUsingPortal>
    </div>
  );
};

export default IcdDescriptionTooltip;
